import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiMessageCircle, FiX, FiHelpCircle, FiMail, FiBook, FiExternalLink } from 'react-icons/fi';

function SupportWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const widgetRef = useRef(null);

  // Determine if user has priority support (Growth and Scale tiers)
  const priorityTiers = ['growth', 'scale'];
  const isPriority = priorityTiers.includes(user?.subscription_tier);

  // Close widget when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Show welcome message for new users
  useEffect(() => {
    const hasSeenWidget = localStorage.getItem('firstcut_seen_support');
    if (!hasSeenWidget && user) {
      setHasUnread(true);
      localStorage.setItem('firstcut_seen_support', 'true');
    }
  }, [user]);

  // Hide on landing page - MUST be after all hooks
  if (location.pathname === '/') {
    return null;
  }

  return (
    <div ref={widgetRef} className="fixed bottom-6 right-6 z-50">
      {/* Expanded Widget */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 bg-card rounded-2xl shadow-2xl border border-border/50 overflow-hidden animate-in slide-in-from-bottom-5 duration-200">
          {/* Header */}
          <div className="p-4 bg-gradient-to-r from-primary to-accent">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-white">Need Help?</h3>
                <p className="text-sm text-white/80">We're here for you</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors text-white"
              >
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Priority Badge */}
          {isPriority && (
            <div className="px-4 py-2 bg-accent/10 border-b border-accent/20">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-accent text-accent-foreground text-xs font-bold rounded">PRIORITY</span>
                <span className="text-accent text-xs">
                  {user?.subscription_tier?.charAt(0).toUpperCase() + user?.subscription_tier?.slice(1)} support
                </span>
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div className="p-4 space-y-2">
            <Link
              to="/help"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors group"
            >
              <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                <FiBook className="text-primary" size={18} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-foreground text-sm">Help Center</div>
                <div className="text-xs text-muted-foreground">Browse FAQs and guides</div>
              </div>
              <FiExternalLink className="text-muted-foreground" size={16} />
            </Link>

            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors group"
            >
              <div className="p-2 bg-emerald-500/10 rounded-lg group-hover:bg-emerald-500/20 transition-colors">
                <FiMail className="text-emerald-400" size={18} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-foreground text-sm">Contact Support</div>
                <div className="text-xs text-muted-foreground">
                  {isPriority ? '4-12 hour response' : '24-48 hour response'}
                </div>
              </div>
              <FiExternalLink className="text-muted-foreground" size={16} />
            </Link>

            {/* Priority-only: Direct Contact */}
            {isPriority && (
              <a
                href="mailto:priority@firstcut.app"
                className="flex items-center gap-3 p-3 rounded-lg bg-accent/10 hover:bg-accent/20 transition-colors group"
              >
                <div className="p-2 bg-accent/20 rounded-lg group-hover:bg-accent/30 transition-colors">
                  <FiMessageCircle className="text-accent" size={18} />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-accent text-sm">Direct Line</div>
                  <div className="text-xs text-accent/70">priority@firstcut.app</div>
                </div>
              </a>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-secondary/50 border-t border-border/30">
            <p className="text-xs text-muted-foreground text-center">
              Response times vary by plan.{' '}
              <Link to="/usage" className="text-primary hover:underline" onClick={() => setIsOpen(false)}>
                View your plan
              </Link>
            </p>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          setHasUnread(false);
        }}
        className={`relative p-4 rounded-full shadow-lg transition-all hover:scale-110 ${
          isOpen
            ? 'bg-card text-foreground border border-border'
            : 'bg-gradient-to-r from-primary to-accent text-white'
        }`}
        data-testid="support-widget-btn"
      >
        {isOpen ? (
          <FiX size={24} />
        ) : (
          <FiHelpCircle size={24} />
        )}
        
        {/* Unread indicator */}
        {hasUnread && !isOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full border-2 border-background" />
        )}

        {/* Priority badge on button */}
        {isPriority && !isOpen && (
          <span className="absolute -bottom-1 -right-1 px-1.5 py-0.5 bg-accent text-accent-foreground text-[10px] font-bold rounded">
            PRO
          </span>
        )}
      </button>
    </div>
  );
}

export default SupportWidget;
